/**
 * The phone's cached vault keys (the unwrapped master key and the wrapping
 * secret key), bound to the account they belong to.
 *
 * An ordinary sign-out used to delete them, so every sign-in on the phone
 * asked for the recovery code again (task of 2026-09-22). They now survive
 * a sign-out, but only for the same account:
 *
 *   - caching a key records its owner (VaultKeyOwner, the session's user id);
 *   - a read returns the key only to that owner, signed in; with nobody
 *     signed in it returns nothing, and with anyone else signed in it
 *     deletes both keys first (fail closed);
 *   - sign-out binds a key that has no owner yet to the account signing
 *     out, or deletes it when that account cannot be told;
 *   - account deletion still deletes everything (logoutWipe).
 *
 * The web is unchanged: a browser never keeps the key (decision of
 * 2026-09-18), so web sign-out deletes it as before and reads are not
 * owner-checked (the key only ever lives in that tab's memory).
 */
import { Platform } from 'react-native';

import { SecureStorage, SecureStorageKeys } from '../storage/SecureStorage';
import { getSignedInUserIdFromSession } from '../auth/currentUser';
import { createLogger } from '../../utils/logger';

const logger = createLogger('vaultKeyCache');

type VaultKey = typeof SecureStorageKeys.masterRecoveryKey | typeof SecureStorageKeys.wrappingSecretKey;

const VAULT_KEYS: VaultKey[] = [SecureStorageKeys.masterRecoveryKey, SecureStorageKeys.wrappingSecretKey];

const isWeb = () => Platform.OS === 'web';

/** Delete both cached keys and their owner. */
export async function discardCachedVaultKeys(): Promise<void> {
  await Promise.all([
    ...VAULT_KEYS.map((k) => SecureStorage.removeItem(k)),
    SecureStorage.removeItem(SecureStorageKeys.vaultKeyOwner),
  ]);
}

/**
 * Cache a vault key for the signed-in account. Anything cached for another
 * account (or cached with an owner while nobody is signed in) is deleted
 * first, so a new owner never inherits someone else's leftover key.
 */
export async function cacheVaultKey(key: VaultKey, value: string): Promise<void> {
  if (isWeb()) {
    await SecureStorage.setItem(key, value, 'medium');
    return;
  }
  const userId = await getSignedInUserIdFromSession();
  const owner = await SecureStorage.getItem(SecureStorageKeys.vaultKeyOwner);
  if (owner && owner !== userId) await discardCachedVaultKeys();
  await SecureStorage.setItem(key, value, 'medium');
  if (userId) await SecureStorage.setItem(SecureStorageKeys.vaultKeyOwner, userId, 'medium');
}

/**
 * A cached vault key, for its owner only. Null when nothing is cached, when
 * nobody is signed in, or when the key belongs to another account (which
 * deletes it).
 */
export async function readBoundVaultKey(key: VaultKey): Promise<string | null> {
  const value = await SecureStorage.getItem(key);
  if (!value || isWeb()) return value;

  const userId = await getSignedInUserIdFromSession();
  if (!userId) return null;

  const owner = await SecureStorage.getItem(SecureStorageKeys.vaultKeyOwner);
  if (!owner) {
    // Cached in this session before an owner was recorded (or by a build
    // before this one, whose sign-out always deleted the key): it can only
    // be the signed-in account's.
    await SecureStorage.setItem(SecureStorageKeys.vaultKeyOwner, userId, 'medium');
    return value;
  }
  if (owner !== userId) {
    logger.warn('Cached vault key belongs to another account on this device; deleting it');
    await discardCachedVaultKeys();
    return null;
  }
  return value;
}

/**
 * Before an ordinary sign-out wipes the session: keep the keys, bound to
 * the account signing out. On the web, or when that account cannot be
 * told, delete them instead.
 */
export async function prepareVaultKeysForSignOut(): Promise<void> {
  if (isWeb()) {
    await discardCachedVaultKeys();
    return;
  }
  const cached = await Promise.all(VAULT_KEYS.map((k) => SecureStorage.getItem(k)));
  if (!cached.some(Boolean)) {
    await SecureStorage.removeItem(SecureStorageKeys.vaultKeyOwner);
    return;
  }
  const owner = await SecureStorage.getItem(SecureStorageKeys.vaultKeyOwner);
  const userId = await getSignedInUserIdFromSession();
  if (owner && userId && owner !== userId) {
    await discardCachedVaultKeys();
    return;
  }
  if (!owner) {
    if (userId) {
      await SecureStorage.setItem(SecureStorageKeys.vaultKeyOwner, userId, 'medium');
    } else {
      await discardCachedVaultKeys();
    }
  }
}
